'use client';

import {
  type FileDto,
  LocaleEnum,
  type Workspace,
  WorkspacesApi,
} from '@up2stay/up2promo-api-client';
import { toExternalHref } from '@up2stay/up2promo-commons';
import { isValidHttpUrl } from '@up2stay/up2promo-commons/products';
import {
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@up2stay/up2promo-ui';
import { Save } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

import { UsernameInput } from '@/core/components/auth/username-input';
import { LogoUpload } from '@/core/components/settings/logo-upload';
import {
  SettingsGroupRow,
  SettingsGroupRows,
} from '@/core/components/settings/settings-group';
import { SettingsInfoHint } from '@/core/components/settings/settings-info-hint';
import { SettingsOpsStrip } from '@/core/components/settings/settings-ops-strip';
import { SettingsSection } from '@/core/components/settings/settings-section';
import { useAutosaveCue } from '@/core/hooks/use-autosave-cue';

type BasicInfoDict = {
  title: string;
  description: string;
  nameLabel: string;
  namePlaceholder: string;
  nameRequired: string;
  usernameLabel?: string;
  usernamePlaceholder?: string;
  usernameSectionHint?: string;
  usernameInvalid?: string;
  usernameAlreadyTaken?: string;
  usernameHelperText?: string;
  usernameChangeTitle?: string;
  usernameChangeDescription?: string;
  usernameChangeConfirm?: string;
  usernameChangeCancel?: string;
  descriptionLabel: string;
  descriptionPlaceholder: string;
  websiteLabel: string;
  websitePlaceholder: string;
  emailLabel: string;
  emailPlaceholder: string;
  websiteInvalid: string;
  emailInvalid: string;
  defaultLocaleLabel?: string;
  defaultLocaleHelper?: string;
  defaultLocaleGerman?: string;
  defaultLocaleEnglish?: string;
  defaultLocaleSaveError?: string;
};

type LogoDict = {
  label: string;
  description: string;
  upload: string;
  change: string;
  remove: string;
  uploaded: string;
  uploadFailed: string;
  acceptsImages: string;
  uploading: string;
  dragAndDrop: string;
};

type WorkspaceOverviewSectionProps = {
  sectionId: string;
  workspace: Workspace;
  onWorkspaceUpdated: (workspace: Workspace) => void;
  workspaceDict: {
    overview?: { title: string; description: string };
    basicInfo: BasicInfoDict;
    logoUpload: LogoDict;
    saveError: string;
    saveSuccess: string;
  };
  saveLabel: string;
  savingLabel: string;
};

type AutosaveFields = {
  name: string;
  description: string;
  website: string;
  email: string;
};

/** Same shape the API's `@IsEmail()` accepts in practice (local@domain.tld). */
const CONTACT_EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

function fieldsSnapshot(fields: AutosaveFields): string {
  return JSON.stringify({
    name: fields.name.trim(),
    description: fields.description.trim(),
    website: fields.website.trim(),
    email: fields.email.trim(),
  });
}

function initialsFrom(name: string): string {
  return (
    name
      .split(' ')
      .map((part) => part[0])
      .join('')
      .toUpperCase()
      .slice(0, 2) || 'W'
  );
}

export function BasicInfoSection({
  sectionId,
  workspace,
  onWorkspaceUpdated,
  workspaceDict,
  saveLabel,
  savingLabel,
}: WorkspaceOverviewSectionProps) {
  const basic = workspaceDict.basicInfo;
  const [name, setName] = useState(workspace.name || '');
  const [description, setDescription] = useState(workspace.description || '');
  const [website, setWebsite] = useState(workspace.website || '');
  const [email, setEmail] = useState(workspace.email || '');
  const [username, setUsername] = useState(workspace.username || '');
  const [usernameError, setUsernameError] = useState<string | undefined>();
  const [websiteError, setWebsiteError] = useState<string | undefined>();
  const [emailError, setEmailError] = useState<string | undefined>();
  const [isSavingUsername, setIsSavingUsername] = useState(false);
  const [confirmUsernameOpen, setConfirmUsernameOpen] = useState(false);
  const [isSavingLocale, setIsSavingLocale] = useState(false);
  const [defaultLocale, setDefaultLocale] = useState<LocaleEnum>(
    workspace.defaultLocale ?? LocaleEnum.DE,
  );
  const [logoPreview, setLogoPreview] = useState<string | undefined>(
    workspace.photo?.path,
  );

  const {
    status: autosaveStatus,
    markSaving,
    markSaved,
    markError,
  } = useAutosaveCue();

  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [lastSynced, setLastSynced] = useState(() =>
    fieldsSnapshot({
      name: workspace.name || '',
      description: workspace.description || '',
      website: workspace.website || '',
      email: workspace.email || '',
    }),
  );

  const workspaceSyncKey = [
    workspace.id,
    workspace.name,
    workspace.description,
    workspace.website,
    workspace.email,
    workspace.username,
    workspace.defaultLocale,
    workspace.photo?.path,
  ].join('\0');
  const [prevWorkspaceSyncKey, setPrevWorkspaceSyncKey] =
    useState(workspaceSyncKey);
  const [prevWorkspace, setPrevWorkspace] = useState(workspace);
  if (workspaceSyncKey !== prevWorkspaceSyncKey) {
    setPrevWorkspaceSyncKey(workspaceSyncKey);
    setPrevWorkspace(workspace);
    // Only take over values that changed on the server: our own save echoes
    // back, and a field kept unsaved because it is invalid must keep the
    // user's input next to its error (DEV-551).
    if (workspace.name !== prevWorkspace.name) setName(workspace.name || '');
    if (workspace.description !== prevWorkspace.description) {
      setDescription(workspace.description || '');
    }
    if (workspace.website !== prevWorkspace.website) {
      setWebsite(workspace.website || '');
    }
    if (workspace.email !== prevWorkspace.email) {
      setEmail(workspace.email || '');
    }
    if (workspace.id !== prevWorkspace.id) {
      setName(workspace.name || '');
      setDescription(workspace.description || '');
      setWebsite(workspace.website || '');
      setEmail(workspace.email || '');
      setWebsiteError(undefined);
      setEmailError(undefined);
    }
    setUsername(workspace.username || '');
    setDefaultLocale(workspace.defaultLocale ?? LocaleEnum.DE);
    setLogoPreview(workspace.photo?.path);
    setLastSynced(
      fieldsSnapshot({
        name: workspace.name || '',
        description: workspace.description || '',
        website: workspace.website || '',
        email: workspace.email || '',
      }),
    );
  }

  const persistFields = useCallback(async () => {
    const trimmedName = name.trim();
    if (!trimmedName) return;

    // An invalid field is flagged and keeps its last saved value; the other
    // fields still save instead of the whole PATCH failing with 422 (DEV-551).
    const synced = JSON.parse(lastSynced) as AutosaveFields;
    const websiteInput = website.trim();
    const websiteInvalid = !!websiteInput && !isValidHttpUrl(websiteInput);
    const emailInput = email.trim();
    const emailInvalid =
      !!emailInput && !CONTACT_EMAIL_PATTERN.test(emailInput);
    setWebsiteError(websiteInvalid ? basic.websiteInvalid : undefined);
    setEmailError(emailInvalid ? basic.emailInvalid : undefined);
    const websiteValue = websiteInvalid ? synced.website : websiteInput;
    const emailValue = emailInvalid ? synced.email : emailInput;
    const hasInvalidField = websiteInvalid || emailInvalid;

    const snapshot = fieldsSnapshot({
      name,
      description,
      website: websiteValue,
      email: emailValue,
    });
    if (snapshot === lastSynced) {
      if (hasInvalidField) markError();
      return;
    }

    markSaving();
    try {
      const response = await WorkspacesApi.update({
        path: { id: workspace.id },
        body: {
          name: trimmedName,
          description: description.trim(),
          website: websiteValue ? toExternalHref(websiteValue) : '',
          email: emailValue,
        },
      });
      if (!response.data) {
        markError();
        toast.error(workspaceDict.saveError);
        return;
      }
      onWorkspaceUpdated(response.data);
      setLastSynced(
        fieldsSnapshot({
          name: response.data.name || '',
          description: response.data.description || '',
          website: response.data.website || '',
          email: response.data.email || '',
        }),
      );
      // The flagged field was not saved: keep the cue on "save failed".
      if (hasInvalidField) markError();
      else markSaved();
    } catch {
      markError();
      toast.error(workspaceDict.saveError);
    }
  }, [
    basic.emailInvalid,
    basic.websiteInvalid,
    description,
    email,
    markError,
    markSaved,
    markSaving,
    name,
    onWorkspaceUpdated,
    website,
    lastSynced,
    workspace.id,
    workspaceDict.saveError,
  ]);

  // Leaving the page within the debounce window cancelled the save and lost
  // the last edit (DEV-573); the unmount flush below catches that.
  const persistFieldsRef = useRef(persistFields);
  const hasPendingSaveRef = useRef(false);
  useEffect(() => {
    persistFieldsRef.current = persistFields;
  }, [persistFields]);

  useEffect(() => {
    if (!name.trim()) return;

    const snapshot = fieldsSnapshot({ name, description, website, email });
    if (snapshot === lastSynced) return;

    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    hasPendingSaveRef.current = true;
    saveTimeoutRef.current = setTimeout(() => {
      hasPendingSaveRef.current = false;
      void persistFields();
    }, 700);

    return () => {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    };
  }, [description, email, lastSynced, name, persistFields, website]);

  useEffect(
    () => () => {
      if (!hasPendingSaveRef.current) return;
      hasPendingSaveRef.current = false;
      void persistFieldsRef.current();
    },
    [],
  );

  const persistPhoto = async (photo: FileDto | null) => {
    markSaving();
    try {
      const response = await WorkspacesApi.update({
        path: { id: workspace.id },
        body: {
          photo: photo as unknown as FileDto | undefined,
        },
      });
      if (!response.data) {
        markError();
        toast.error(workspaceDict.logoUpload.uploadFailed);
        return;
      }
      onWorkspaceUpdated(response.data);
      setLogoPreview(response.data.photo?.path);
      markSaved();
    } catch {
      markError();
      toast.error(workspaceDict.logoUpload.uploadFailed);
    }
  };

  const handleSaveUsername = async () => {
    const trimmed = username.trim();
    if (!trimmed) return;
    if (trimmed === (workspace.username || '')) return;

    setIsSavingUsername(true);
    markSaving();
    setUsernameError(undefined);
    try {
      const response = await WorkspacesApi.update({
        path: { id: workspace.id },
        body: { username: trimmed },
      });
      if (!response.data) {
        markError();
        toast.error(workspaceDict.saveError);
        return;
      }
      onWorkspaceUpdated(response.data);
      markSaved();
      toast.success(workspaceDict.saveSuccess);
    } catch (error) {
      const axiosError = error as {
        response?: { data?: { errors?: { username?: string } } };
      };
      const usernameErr = axiosError?.response?.data?.errors?.username;
      markError();
      if (usernameErr === 'usernameAlreadyTaken') {
        setUsernameError(
          basic.usernameAlreadyTaken || 'This username is already taken',
        );
      } else if (usernameErr === 'invalidUsername') {
        setUsernameError(basic.usernameInvalid || 'Invalid username format');
      } else {
        toast.error(workspaceDict.saveError);
      }
    } finally {
      setIsSavingUsername(false);
    }
  };

  const handleDefaultLocaleChange = async (value: string) => {
    const next = value === LocaleEnum.EN ? LocaleEnum.EN : LocaleEnum.DE;
    if (next === (workspace.defaultLocale ?? LocaleEnum.DE)) {
      setDefaultLocale(next);
      return;
    }

    const previous = defaultLocale;
    setDefaultLocale(next);
    setIsSavingLocale(true);
    markSaving();
    try {
      const response = await WorkspacesApi.update({
        path: { id: workspace.id },
        body: { defaultLocale: next },
      });
      if (!response.data) {
        setDefaultLocale(previous);
        markError();
        toast.error(basic.defaultLocaleSaveError || workspaceDict.saveError);
        return;
      }
      onWorkspaceUpdated(response.data);
      setDefaultLocale(response.data.defaultLocale ?? next);
      markSaved();
      toast.success(workspaceDict.saveSuccess);
    } catch {
      setDefaultLocale(previous);
      markError();
      toast.error(basic.defaultLocaleSaveError || workspaceDict.saveError);
    } finally {
      setIsSavingLocale(false);
    }
  };

  const usernameDirty = username.trim() !== (workspace.username || '');

  const sectionTitle =
    workspaceDict.overview?.title ?? basic.title ?? 'Overview';
  const sectionDescription =
    workspaceDict.overview?.description ?? basic.description;

  const usernameLabel = basic.usernameLabel || 'URL short name';
  const usernameInfo = basic.usernameHelperText?.replace(
    '{username}',
    username.trim() || '…',
  );
  const localeLabel = basic.defaultLocaleLabel || 'Default language';

  return (
    <>
      <SettingsSection
        id={sectionId}
        title={sectionTitle}
        footer={sectionDescription}
        grouped
      >
        <SettingsGroupRows>
          <SettingsGroupRow>
            <SettingsOpsStrip
              surface="plain"
              photoPath={logoPreview}
              name={name.trim() || workspace.name || ''}
              username={workspace.username}
              initials={initialsFrom(name.trim() || workspace.name || 'W')}
              autosaveStatus={autosaveStatus}
              photoControls={
                <LogoUpload
                  label={workspaceDict.logoUpload.label}
                  description={workspaceDict.logoUpload.description}
                  currentPreview={logoPreview}
                  onUpload={(fileId) => {
                    void persistPhoto({ id: fileId });
                  }}
                  onRemove={() => {
                    void persistPhoto(null);
                  }}
                  compact
                  hideCaption
                />
              }
            />
          </SettingsGroupRow>

          <SettingsGroupRow separated>
            <div className="space-y-2">
              <Label htmlFor="workspaceName">{basic.nameLabel}</Label>
              <Input
                id="workspaceName"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={basic.namePlaceholder}
              />
            </div>
          </SettingsGroupRow>

          <SettingsGroupRow separated>
            <div className="space-y-2">
              <div className="flex items-center gap-0.5">
                <Label htmlFor="workspaceUsername">{usernameLabel}</Label>
                {usernameInfo ? (
                  <SettingsInfoHint label={usernameLabel}>
                    {usernameInfo}
                  </SettingsInfoHint>
                ) : null}
              </div>
              <div className="flex flex-col gap-2 sm:flex-row sm:items-start">
                <div className="min-w-0 flex-1">
                  <UsernameInput
                    id="workspaceUsername"
                    value={username}
                    onChange={setUsername}
                    workspaceName={name}
                    error={usernameError}
                    excludeWorkspaceId={workspace.id}
                    placeholder={basic.usernamePlaceholder}
                  />
                </div>
                {usernameDirty ? (
                  <Button
                    type="button"
                    onClick={() => setConfirmUsernameOpen(true)}
                    disabled={isSavingUsername}
                    isLoading={isSavingUsername}
                  >
                    <Save className="size-4" />
                    {isSavingUsername ? savingLabel : saveLabel}
                  </Button>
                ) : null}
              </div>
              {usernameError ? (
                <p className="text-sm text-destructive">{usernameError}</p>
              ) : null}
            </div>
          </SettingsGroupRow>

          <SettingsGroupRow separated>
            <div className="space-y-2">
              <Label htmlFor="workspaceDescription">
                {basic.descriptionLabel}
              </Label>
              <Textarea
                id="workspaceDescription"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={basic.descriptionPlaceholder}
                rows={3}
              />
            </div>
          </SettingsGroupRow>

          <SettingsGroupRow separated>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="workspaceWebsite">{basic.websiteLabel}</Label>
                <Input
                  id="workspaceWebsite"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                  type="text"
                  inputMode="url"
                  autoComplete="url"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  placeholder={basic.websitePlaceholder}
                  className={websiteError ? 'border-destructive' : ''}
                  aria-invalid={websiteError ? true : undefined}
                  aria-describedby={
                    websiteError ? 'workspaceWebsite-error' : undefined
                  }
                />
                {websiteError ? (
                  <p
                    id="workspaceWebsite-error"
                    className="text-sm text-destructive"
                  >
                    {websiteError}
                  </p>
                ) : null}
              </div>
              <div className="space-y-2">
                <Label htmlFor="workspaceEmail">{basic.emailLabel}</Label>
                <Input
                  id="workspaceEmail"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={basic.emailPlaceholder}
                  className={emailError ? 'border-destructive' : ''}
                  aria-invalid={emailError ? true : undefined}
                  aria-describedby={
                    emailError ? 'workspaceEmail-error' : undefined
                  }
                />
                {emailError ? (
                  <p
                    id="workspaceEmail-error"
                    className="text-sm text-destructive"
                  >
                    {emailError}
                  </p>
                ) : null}
              </div>
            </div>
          </SettingsGroupRow>

          <SettingsGroupRow separated>
            <div className="space-y-2">
              <div className="flex items-center gap-0.5">
                <Label htmlFor="workspaceDefaultLocale">{localeLabel}</Label>
                {basic.defaultLocaleHelper ? (
                  <SettingsInfoHint label={localeLabel}>
                    {basic.defaultLocaleHelper}
                  </SettingsInfoHint>
                ) : null}
              </div>
              <Select
                value={defaultLocale}
                onValueChange={(value) => {
                  void handleDefaultLocaleChange(value);
                }}
                disabled={isSavingLocale}
              >
                <SelectTrigger
                  id="workspaceDefaultLocale"
                  className="w-full sm:max-w-xs"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={LocaleEnum.DE}>
                    {basic.defaultLocaleGerman || 'German'}
                  </SelectItem>
                  <SelectItem value={LocaleEnum.EN}>
                    {basic.defaultLocaleEnglish || 'English'}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </SettingsGroupRow>
        </SettingsGroupRows>
      </SettingsSection>
      <AlertDialog
        open={confirmUsernameOpen}
        onOpenChange={setConfirmUsernameOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {basic.usernameChangeTitle ?? 'Change the workspace username?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {basic.usernameChangeDescription}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              {basic.usernameChangeCancel ?? 'Cancel'}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setConfirmUsernameOpen(false);
                void handleSaveUsername();
              }}
            >
              {basic.usernameChangeConfirm ?? saveLabel}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
